"use client"

import { portfolioData } from "@/data/portfolioData"
import useResponsive from "@/lib/useResponsive"
import { Button } from "@/components/ui-lab/button"
import { Card, CardContent } from "@/components/ui-lab/card"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui-lab/tooltip"
import Image from "next/image"
import { BsSave } from "react-icons/bs"
import { FaGithub, FaLinkedin } from "react-icons/fa"
import { MdEmail, MdPhone } from "react-icons/md"

export const MyHero = () => {
  const { isMobile } = useResponsive()
  const {
    personal_info,
    bio: { title1, title2 },
  } = portfolioData
  const [primaryTitle, secondaryTitle] = personal_info.title.split("|").map((part) => part.trim())

  const contactItems = [
    {
      icon: FaGithub,
      href: personal_info.links.github,
      label: "GitHub",
      rel: "noopener noreferrer me",
    },
    {
      icon: FaLinkedin,
      href: personal_info.links.linkedin,
      label: "LinkedIn",
      rel: "noopener noreferrer me",
    },
    {
      icon: MdEmail,
      href: `mailto:${personal_info.email}`,
      label: "Email",
      rel: undefined,
    },
    {
      icon: MdPhone,
      href: `tel:${personal_info.phone}`,
      label: "Phone",
      rel: undefined,
    },
  ]
  return (
    <Card className="border-0 shadow-2xl bg-white dark:bg-gray-800">
      <CardContent className="p-6 grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-3">
        <div className="self-start md:p-1">
          <div className="flex flex-row">
            {isMobile && (
              <>
                <div className="flex flex-col justify-center items-center my-auto gap-2">
                  {contactItems.map((item, index) => (
                    <Button
                      key={index}
                      variant="ghost"
                      size="icon-lg"
                      asChild
                      className="transition-all duration-300 hover:scale-125 hover:text-teal-600 dark:hover:text-teal-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <a href={item.href} target="_blank" rel={item.rel} aria-label={item.label}>
                        <item.icon className="text-2xl" aria-hidden="true" />
                      </a>
                    </Button>
                  ))}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="icon-lg"
                        asChild
                        className="transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-teal-500/50"
                      >
                        <a href="/Amit_Dhoju_resume.pdf" target="_blank" aria-label="Download My Resume">
                          <BsSave />
                        </a>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right">Download My Resume</TooltipContent>
                  </Tooltip>
                </div>
              </>
            )}
            <div className="mx-auto !text-center w-60 h-60 bg-gradient-to-br from-teal-500 to-cyan-400 flex !items-center !justify-center text-white text-6xl font-bold shadow-xl rounded-3xl transition-all duration-500 hover:shadow-2xl hover:shadow-teal-500/50 hover:scale-105 overflow-hidden group">
              <Image
                src="/images/profile-picture.jpg"
                alt="Amit Dhoju — JavaScript Software Engineer based in Nepal"
                height={250}
                width={250}
                sizes="(max-width: 640px) 240px, (max-width: 1024px) 240px, 250px"
                className="object-cover w-full h-full group-hover:scale-110 transition-transform duration-500 text-center items-center rounded-2xl"
                priority
                fetchPriority="high"
                placeholder="blur"
                blurDataURL="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 300'%3E%3Crect fill='%23e5e7eb' width='400' height='300'/%3E%3C/svg%3E"
              />
            </div>
          </div>

          {!isMobile && (
            <>
              <div className="md:flex gap-4 justify-center mt-2 hidden">
                {contactItems.map((item, index) => (
                  <Button
                    key={index}
                    variant="ghost"
                    size="icon-lg"
                    asChild
                    className="transition-all duration-300 hover:scale-115 hover:text-teal-600 dark:hover:text-teal-400 hover:bg-teal-100 dark:hover:bg-teal-700"
                  >
                    <a href={item.href} target="_blank" rel={item.rel} aria-label={item.label}>
                      <item.icon className="text-xl" aria-hidden="true" />
                    </a>
                  </Button>
                ))}
              </div>
              <div className="justify-center mt-2 hidden md:flex">
                {/* <Button size="lg" asChild className="transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-teal-500/50">
                  <a href="/Amit_Dhoju_resume.pdf" target="_blank">
                    <BsSave />
                    Save My Resume
                  </a>
                </Button> */}
              </div>
            </>
          )}
        </div>
        <div className="md:col-span-2 self-start rounded-lg" suppressHydrationWarning>
          <div className="flex flex-row justify-between mr-4">
            <div className="flex items-end gap-2">
              <h1 className="mb-2 mt-2">
                <span className="block text-4xl font-semibold">{personal_info.name}</span>
                <span className="mt-1 block text-xl font-semibold text-muted-foreground md:whitespace-nowrap">
                  <span className="block md:inline">{primaryTitle}</span>
                  <span className="hidden md:inline"> | </span>
                  <span className="block md:inline">{secondaryTitle}</span>
                </span>
              </h1>

              {/* <h5 className="mb-0 pb-0.5 pt-2 mt-2 text-sm font-semibold text-muted-foreground">
                Node.js | React | TypeScript | Next.js | CI/CD | AWS Deployment
              </h5> */}
            </div>
          </div>

          <p className="text-base mb-4">{title1}</p>
          <p className="text-base mb-4">{title2}</p>
          <div className="justify-start mt-2 hidden md:flex">
            <Button
              size="lg"
              asChild
              className="transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-teal-500/50"
            >
              <a href="/Amit_Dhoju_resume.pdf" target="_blank">
                <BsSave />
                Save My Resume
              </a>
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
